// filepath: /Users/evan/Documents/gnoknews/src/app/pages/home/home.page.ts
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ArticleService } from '../../services/article.service';
import { TopicService } from '../../services/topic.service';
import { AuthService } from '../../services/auth.service';
import { PreviewBlock } from '../../entities/article_preview.model';
import { User } from '../../entities/user.model';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IonicModule, IonInfiniteScroll } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Timestamp } from '@angular/fire/firestore';
import { Topic } from '../../entities/topic.model';
import { Subscription, forkJoin } from 'rxjs';
import { Article } from 'src/app/entities/article.model';
import { TourService } from '../../services/tour.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
})
export class HomePage implements OnInit {
  previewBlocks: PreviewBlock[] = [];
  user: User | null = null;
  private userSubscription!: Subscription; // Manage subscription lifecycle
  targetLang: string = 'ru'; // Default language
  nativeLang: string = 'en'; // Default language
  level: number = 1; // Default level
  topics: Topic[] = [];
  selectedTopic: string = '';
  loadingMore: boolean = false;
  allLoaded: boolean = false;
  lastDate: Date | null = null;
  pageSize: number = 10;
  isBookmarksView: boolean = false; // Add this flag

  constructor(
    private articleService: ArticleService, 
    public authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private topicService: TopicService,
    private route: ActivatedRoute,
    private tourService: TourService,
    private userService: UserService
  ) {}

  ngOnInit() {
    document.title = 'Home';
    this.route.paramMap.subscribe(params => {
      const topicName = params.get('topic');
      this.isBookmarksView = topicName === 'bookmarks';
      if (topicName) {
        this.selectedTopic = topicName;
        if (this.user) {
          this.resetAndLoadArticles();
        }
      }
    });
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.user = user;
      if (this.user) {
        this.targetLang = this.user.targetLanguage;
        this.nativeLang = this.user.nativeLanguage;
        this.level = this.user.level[this.targetLang];
        this.loadTopics();
      }
    });
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe(); // Prevent memory leaks
    }
  }

  resetAndLoadArticles() {
    this.previewBlocks = [];
    this.lastDate = null;
    this.allLoaded = false;
    this.loadArticles();
  }

  loadArticles(event?: any) {
    if (this.allLoaded || this.loadingMore) {
      if (event) event.target.complete();
      return;
    }
    this.loadingMore = true;

    // Bookmarks view logic
    if (this.isBookmarksView && this.user && this.user.bookmarks.length > 0) {
      // Get all bookmarked articles by ID
      const articleObservables = this.user.bookmarks.map(id =>
      this.articleService.getArticleById(id)
      );
      forkJoin(articleObservables).subscribe((articles) => {
      // Filter out undefined (not found) articles
      const validArticles = articles.filter(a => !!a && a.language === this.targetLang) as Article[];
      // Create a PreviewBlock for each bookmarked article
      const previewBlocks: PreviewBlock[] = validArticles.map(article => ({
        articles: [{
        app_article: article.id,
        date: article.publishDate.toDate().toISOString(),
        publishDate: article.publishDate,
        link: article.originalUrl || '',
        position: 0,
        serpapi_link: '',
        source: {
          icon: article.publisherIcon || '',
          name: article.publisher || '',
        },
        story_token: '',
        thumbnail: article.imageUrl || '',
        thumbnail_small: article.imageUrl || '',
        title: article.title,
        }],
        category: 'bookmarks',
        date_added: article.publishDate ? article.publishDate : Timestamp.now(),
        language: article.language,
        story_token: 'bookmarks'
      }));
      this.previewBlocks = previewBlocks;
      this.loadingMore = false;
      if (event) event.target.complete();
      });
      return;
    }

    let topicToken = '';
    // Switch default topic for target lang
    if (this.selectedTopic == '') {
      switch (this.targetLang) {
        case 'en':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtVnVHZ0pWVXlnQVAB';
          break;
        case 'es':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtVnpHZ0pGVXlnQVAB';
          break;
        case 'fr':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtWnlHZ0pHVWlnQVAB';
          break;
        case 'de':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtUmxHZ0pFUlNnQVAB';
          break;
        case 'pt':
          topicToken = 'CAAqKggKIiRDQkFTRlFvSUwyMHZNRGx1YlY4U0JYQjBMVUpTR2dKQ1VpZ0FQAQ';
          break;
        case 'ja':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtcGhHZ0pLVUNnQVAB';
          break;
        case 'ru':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FuSjFHZ0pTVlNnQVAB';
          break;
        case 'ar':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtRnlHZ0pUUVNnQVAB';
          break;
        case 'hi':
          topicToken = 'CAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtaHBHZ0pKVGlnQVAB';
          break;
        case 'zh':
          topicToken = 'CAAqKggKIiRDQkFTRlFvSUwyMHZNRGx1YlY4U0JYcG9MVU5PR2dKRFRpZ0FQAQ';
          break;
      }
    }
    else {
        //Get the Topic object for the topic string in the URL if the topics are loaded
      let result = this.topics.find( t => t.name === this.selectedTopic);
      if (result) {
        //Use the topic token from the found topic object
        topicToken = result.token;
      }
      else {
        this.router.navigate(['/news']);
        return;
      }
    }
    this.articleService.getArticlePreviewBlocks(
      this.targetLang,
      topicToken,
      this.lastDate,
      this.pageSize
    ).subscribe((blocks) => {
      if (blocks.length < this.pageSize) {
        this.allLoaded = true;
      }
      if (blocks.length > 0) {
        this.previewBlocks = [...this.previewBlocks, ...blocks];
        // Set lastDate for pagination (date_added should exist on PreviewBlock)
        const lastBlock = blocks[blocks.length - 1];
        if (lastBlock && lastBlock.date_added) {
          this.lastDate = lastBlock.date_added.toDate ? lastBlock.date_added.toDate() : new Date(lastBlock.date_added.toDate());
        }
      }
      this.loadingMore = false;
      if (event) event.target.complete();
    });
  }

  loadTopics() {
    this.topicService.getTopics(this.targetLang).subscribe((topics) => {
      this.topics = topics;
      this.resetAndLoadArticles();
    });
  }

  filterTopic(topic: Topic) {
    this.articleService.getArticlePreviewBlocks(this.targetLang, topic.token).subscribe((blocks) => {
      this.previewBlocks = blocks;
    });
  }

  filterTopicByNameAndLang(topicName: string) {
    const topic = this.topics.find(t => t.name === topicName);
    if (topic) {
      this.filterTopic(topic);
    }
  }

  filterByGenre(genre: string) {
    if (genre === 'All') {
      this.loadArticles();
    } else {
      //this.articles = this.articles filter(article => article.genre === genre);
    }
  }

  getRelativeTime(publishDate: Timestamp): string {
    const now = new Date();
    const published = publishDate.toDate();
    const diff = now.getTime() - published.getTime();
    const diffInMinutes = Math.floor(diff / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays  / 30);

    if (diffInMinutes < 60) {
      return this.translate.instant('TIME.MINUTES_AGO', { value: diffInMinutes });
    } else if (diffInHours < 24) {
      return this.translate.instant('TIME.HOURS_AGO', { value: diffInHours });
    } else if (diffInDays < 30) {
      return this.translate.instant('TIME.DAYS_AGO', { value: diffInDays });
    } else {
      return this.translate.instant('TIME.MONTHS_AGO', { value: diffInMonths });
    }
  }

  // Helper method for safe type casting
  getArticleTitle(title: any, lang: string, level: number): string {
    const levelKey = level.toString() as '1' | '2' | '3';
    return title?.[lang]?.[levelKey] || '';
  }
}